import React, { FC } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button from '@empiriecom/module-components/Button';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking, { GtmEventCategoryData } from '@empiriecom/module-components/hooks/useTracking';
import { FragmentConfiguration } from '@/config';
import Breadcrumb from './Breadcrumb';
import { useCategory } from '../ProductContext';
import useHashParameters from '../hooks/useHashParameters';

export interface NavigationProps {
  isMobile: boolean;
}

const NavigationSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dadada;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

/**
 * <p>This components provides a basic navigation to get back to the previous page.</p>
 * <p>The output is determined by hash-parameters from the url:</p>
 * <ul>
 *  <li>for existing `nav-q` it renders 'back to search'</li>
 *  <li>for existing `nav-c` it renders a back button and a BreadCrumb</li>
 *  <li>if none of the above parameters is passed the component renders a simple back button</li>
 * </ul>
 */
export const Navigation: FC<NavigationProps> = ({ isMobile }) => {
  const config = useConfig<FragmentConfiguration>();
  const history = useHistory();
  const ArrowLeft = getIcon('ArrowMediumLeft');
  const dispatchGTMEvent = useTracking();
  const { searchPage, variationId } = useHashParameters();

  const { category, query } = useCategory();

  if (query) {
    dispatchGTMEvent<GtmEventCategoryData>({
      categoryId: category ? category.toString() : null,
      categoryIds: null,
      categoryPath: null,
      event: 'categoryData',
      eventCategory: 'data',
    });
    let href: string = '';
    if (history) {
      href = history.createHref({
        ...history.location,
        hash: `sku=${variationId}`,
        pathname: `/s/${query}/`,
        search: `Page=${searchPage}`,
      });
    }
    return (
      <NavigationSection data-testid="navigation-search">
        <Button
          layout="default"
          onClick={() => {
            if (href !== '' && typeof window !== 'undefined') {
              window.location.href = href;
            } else {
              history.goBack();
            }
          }}
          size="medium"
          data-testid="navigation-button-back"
        >
          <ArrowLeft />{' '}
          <FormattedMessage id="navigation.backtosearch" defaultMessage="Zurück zum Suchergebnis" />
        </Button>
      </NavigationSection>
    );
  }
  return (
    <NavigationSection data-testid="navigation-category">
      <Breadcrumb
        backButton={!isMobile}
        backButtonSize="medium"
        companyId={config.companyId}
        category={parseInt(category, 10)}
      />
    </NavigationSection>
  );
};

export default Navigation;
